import * as React from "react"

import { cn } from "@/lib/utils"
import { Skeleton } from "@/shadcn/components/ui/skeleton"

/**
 * The bodies of the plan change confirmation dialogs, which sit inside
 * SimpleModal. Figma: S311lGXE6JSyTnNAXHmtR7, page "dlvr.it / Plan change
 * confirmation modals" (30255:7135). The change summary panel is 30261:4566 and
 * the over-limits list is 30263:7573.
 *
 * ChangeSummary is the muted panel of before → after rows, with an optional
 * total under a divider: "Due today" or "Credit to your account", the amount,
 * and a line about the next bill. BlockerList is the red-dot list of what the
 * new plan can't keep running.
 */

export type ChangeRow = {
  label: string
  /** Omit for a row that only states the new value, e.g. a restart's price. */
  from?: React.ReactNode
  to: React.ReactNode
}

export type ChangeTotal = {
  label: string
  /** `undefined` while the amount is still being worked out; the slot shows a skeleton. */
  amount?: React.ReactNode
  note?: React.ReactNode
}

const PANEL = "tw:rounded-md tw:border tw:border-border tw:bg-muted"

export function ChangeSummary({
  rows,
  total,
  className,
}: {
  rows: ChangeRow[]
  total?: ChangeTotal | null
  className?: string
}) {
  return (
    <div className={cn(PANEL, "tw:flex tw:flex-col tw:gap-2.5 tw:px-5 tw:py-4", className)}>
      {rows.map((row) => (
        <div
          key={row.label}
          className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:text-sm tw:text-muted-foreground"
        >
          <span>{row.label}</span>
          <span className="tw:text-right">
            {row.from !== undefined ? (
              <>
                {row.from}
                <span aria-hidden="true" className="tw:px-2">→</span>
                <span className="tw:sr-only"> to </span>
              </>
            ) : null}
            <span className="tw:font-medium tw:text-foreground">{row.to}</span>
          </span>
        </div>
      ))}

      {total ? (
        <>
          {rows.length ? <div className="tw:h-px tw:w-full tw:bg-border" /> : null}
          <div className="tw:flex tw:flex-col tw:gap-0.5 tw:text-foreground">
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-4">
              <span className="tw:text-sm tw:font-medium">{total.label}</span>
              {total.amount === undefined ? (
                <Skeleton className="tw:h-5 tw:w-16" />
              ) : (
                <span className="tw:text-base tw:font-semibold">{total.amount}</span>
              )}
            </div>
            {total.note ? (
              <p className="tw:text-xs tw:text-muted-foreground">{total.note}</p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  )
}

export function BlockerList({ heading, items }: { heading: string; items: React.ReactNode[] }) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium tw:text-foreground">{heading}</p>
      <ul className={cn(PANEL, "tw:flex tw:flex-col tw:px-4 tw:py-1")}>
        {items.map((item, i) => (
          <li
            key={i}
            className="tw:flex tw:items-start tw:gap-3 tw:border-border tw:py-3 tw:text-sm tw:text-foreground tw:not-first:border-t"
          >
            {/* centred on the first line of text: (20px line − 6px dot) / 2 */}
            <span aria-hidden="true" className="tw:mt-[7px] tw:size-1.5 tw:shrink-0 tw:rounded-full tw:bg-destructive" />
            <span className="tw:min-w-0 tw:flex-1">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
